/**
 * HTML entity decoding shared by every PDF export path (client mirror of
 * functions/htmlEntities.js — keep both in sync).
 *
 * Rich-text values (TipTap / contentEditable) are stored as HTML and often
 * contain entities such as `&nbsp;`. When such a value reaches a template as
 * *plain text* (tags already stripped, or a string that never had tags) and
 * is then run through `escapeHtml`, the `&` is re-escaped and Chromium prints
 * the literal text "&nbsp;" into the PDF. Decoding first makes the escape
 * idempotent: `&lt;b&gt;` → `<b>` → escaped back to `&lt;b&gt;` (safe), and
 * `&nbsp;` → " ".
 *
 * Also used by the pdfkit paths (`doc.text(...)`) which render strings as-is.
 */

export const NAMED_ENTITIES = {
  nbsp: " ",
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  shy: "­",
  ensp: " ",
  emsp: " ",
  thinsp: " ",
  zwnj: "‌",
  zwj: "‍",
  lrm: "‎",
  rlm: "‏",
  ndash: "–",
  mdash: "—",
  lsquo: "‘",
  rsquo: "’",
  ldquo: "“",
  rdquo: "”",
  hellip: "…",
  bull: "•",
  middot: "·",
  copy: "©",
  reg: "®",
  trade: "™",
  deg: "°",
  times: "×",
  divide: "÷",
  plusmn: "±",
  euro: "€",
};

const ENTITY_RE = /&(#x[0-9a-f]{1,6}|#\d{1,7}|[a-z][a-z0-9]{1,31});/gi;

function decodeOne(body) {
  if (body[0] === "#") {
    const isHex = body[1] === "x" || body[1] === "X";
    const code = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);
    if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return null;
    try {
      return String.fromCodePoint(code);
    } catch {
      return null;
    }
  }
  const named = NAMED_ENTITIES[body] ?? NAMED_ENTITIES[body.toLowerCase()];
  return named === undefined ? null : named;
}

/**
 * Decode HTML entities (`&nbsp;`, `&amp;`, `&#39;`, `&#x05D0;`…) into their
 * characters. Unknown entities are left untouched. Non-breaking spaces become
 * regular spaces so PDF line wrapping behaves.
 */
export function decodeHtmlEntities(value) {
  if (value == null) return "";
  const str = String(value);
  if (str.indexOf("&") === -1) return str;
  return str
    .replace(ENTITY_RE, (match, body) => {
      const decoded = decodeOne(body);
      return decoded === null ? match : decoded;
    })
    .replace(/ /g, " ");
}

/**
 * Escape text for safe interpolation into an HTML template. Decodes first so a
 * value that already carries entities is never double-escaped.
 */
export function escapeHtmlText(value) {
  return decodeHtmlEntities(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

